import { Alert, Button, SimpleGrid, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { ApiUnreachableError } from '../api/client.js';
import { useUploadAttachment } from '../api/hooks.js';
import { fileToBase64 } from '../files.js';
import { useT } from '../i18n/useT.js';
import { queueAttachment } from '../offline/enqueue.js';
import { CameraCapture } from './CameraCapture.js';
import { ErrorMessage } from './ErrorMessage.js';
import { LogTripModal } from './LogTripModal.js';

/**
 * The two things somebody opens this app on a phone to do.
 *
 * Photograph a receipt, and log a trip. The situation both belong to is standing
 * beside a car holding a piece of paper — and three taps through a sidebar is
 * exactly when a person decides to deal with it later and then does not.
 *
 * Rendered in two places: the installed app at `/app`, where these buttons are
 * the whole screen, and Indstillinger → Mobil-app, where the app is explained
 * and installed. They used to sit at the top of Overblik and were moved off it:
 * the overview is a desk screen about what is outstanding, and two full-width
 * phone buttons above the counts pushed the counts off the first screen.
 *
 * **Both actions happen here, not on another page.** "Log tur" used to navigate
 * to `/koerebog?ny=1`. The installed app has no sidebar, so a trip logged from
 * it would have landed the person in the full bookkeeping UI with no way back
 * except the system back gesture. The form opens in place instead.
 *
 * **The home-screen shortcuts land here.** A long-press on the installed icon
 * offers `?kamera=1` and `?ny=1`, which open the viewfinder or the trip form
 * directly rather than merely the screen that has a button for it. The
 * parameter is removed straight away, so a reload or the back button does not
 * reopen either.
 */
export function CaptureActions(): ReactNode {
  const t = useT();
  const upload = useUploadAttachment();
  const [cameraOpen, camera] = useDisclosure(false);
  const [tripOpen, trip] = useDisclosure(false);
  const [queued, setQueued] = useState(false);
  const [params, setParams] = useSearchParams();

  const openCamera = camera.open;
  const openTrip = trip.open;

  useEffect(() => {
    const wantsCamera = params.get('kamera') === '1';
    const wantsTrip = params.get('ny') === '1';
    if (!wantsCamera && !wantsTrip) {
      return;
    }
    // One at a time. Both parameters together is not a link anything produces,
    // and two modals stacked on a phone would hide the one that matters.
    if (wantsCamera) {
      openCamera();
    } else {
      openTrip();
    }
    const next = new URLSearchParams(params);
    next.delete('kamera');
    next.delete('ny');
    setParams(next, { replace: true });
  }, [params, setParams, openCamera, openTrip]);

  async function send(file: File): Promise<void> {
    const contentBase64 = await fileToBase64(file);
    const body = { filename: file.name, mimeType: file.type, contentBase64 };

    // Same rule as the attachments page: a receipt captured with no signal is
    // queued rather than lost, and a refusal from the server is not queued at
    // all because retrying it would never succeed.
    if (!navigator.onLine) {
      await queueAttachment(body);
      setQueued(true);
      return;
    }
    try {
      const attachment = await upload.mutateAsync(body);
      notifications.show({
        color: 'green',
        title: t('att.uploaded'),
        message: attachment.filename,
      });
    } catch (error) {
      /*
        ApiUnreachableError, not TypeError. The browser's fetch throws a
        TypeError, but ApiClient catches it and rethrows this -- so a check
        for TypeError never matched, and a request that lost its
        connection mid-flight was reported as a failure instead of queued.

        Anything else is a refusal, and is rendered below from `upload.error`.
      */
      if (error instanceof ApiUnreachableError) {
        await queueAttachment(body);
        setQueued(true);
      }
    }
  }

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Button size="xl" onClick={camera.open} loading={upload.isPending}>
          {t('att.takePhoto')}
        </Button>
        <Button size="xl" variant="light" onClick={trip.open}>
          {t('trip.log')}
        </Button>
      </SimpleGrid>

      {queued && (
        <Alert color="blue" variant="light">
          {t('offline.queuedHelp')}
        </Alert>
      )}

      {upload.isError && (
        <ErrorMessage error={upload.error} title={t('att.uploadFailed')} />
      )}

      <CameraCapture
        opened={cameraOpen}
        onClose={camera.close}
        onCapture={(file) => {
          void send(file);
        }}
      />

      <LogTripModal opened={tripOpen} onClose={trip.close} />
    </Stack>
  );
}
